import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ProviderReturn } from '../../models/inventory/provider-return';
import { ProviderReturnSearchCommand } from '../../models/inventory/provider-return-search';
import { ApiResponse, EntityKeyByReference, PaginatedApiResponse } from '@card-software/library';
import { getSkipperErrorHeader } from '../../utils/error-header.util';

@Injectable({
  providedIn: 'root',
})
export class ProviderReturnProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/provider-return';

  public getById(id: string, skipInterceptor = false): Observable<ApiResponse<ProviderReturn>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.get<ApiResponse<ProviderReturn>>(`${this.baseUrl}/${id}`, { headers });
  }

  public search(
    searchCommand: ProviderReturnSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
    skipInterceptor = false,
  ): Observable<ApiResponse<PaginatedApiResponse<ProviderReturn>>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      queryParams += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ProviderReturn>>>(this.baseUrl + '/search' + queryParams, searchCommand, {
      headers,
    });
  }

  public createFromEntityKey(entityKey: EntityKeyByReference, skipInterceptor = false): Observable<ApiResponse<ProviderReturn>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<ProviderReturn>>(this.baseUrl + '/entity-key', entityKey, { headers });
  }

  public update(id: string, body: ProviderReturn, skipInterceptor = false): Observable<ApiResponse<ProviderReturn>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.put<ApiResponse<ProviderReturn>>(`${this.baseUrl}/${id}`, body, { headers });
  }

  public updateStatus(id: string, newStatus: number, skipInterceptor = false): Observable<ApiResponse<ProviderReturn>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.put<ApiResponse<ProviderReturn>>(`${this.baseUrl}/status/${id}`, newStatus, { headers });
  }

  public getAvailableQuantities(id: string, skipInterceptor = false): Observable<ApiResponse<Record<string, number>>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.get<ApiResponse<Record<string, number>>>(`${this.baseUrl}/${id}/available-quantities`, { headers });
  }

  public downloadReport(id: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/report/${id}`, { responseType: 'blob' });
  }
}
